import { useState } from 'react';
import { Plus, Receipt, Trash2 } from 'lucide-react';
import { crm, type OrderRow } from '@/lib/crm-client';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { OrderDialog } from '@/components/OrderDialog';
import { OrderDeleteDialog } from '@/components/OrderDeleteDialog';
import { OrderStatusPill } from '@/components/OrderStatusPill';
import { formatEuro } from '@/lib/utils';

interface Props {
  projectId: string;
  /** Vorbelegter Kontakt für einen neuen Auftrag — der Hauptansprechpartner des Kunden. */
  initialContactId: string | undefined;
  orders: OrderRow[];
  onChanged: () => void;
}

/**
 * Am Projekt hängende Aufträge.
 *
 * Eine Zeile öffnet den Auftrag zum Bearbeiten — dort stehen auch die
 * Positionen und der Statuswechsel —, der Papierkorb löscht ihn nach
 * Rückfrage. Eine eigene Auftragsseite gibt es nicht mehr; bis Aufträge in
 * einem Projektreiter wohnen, ist dies einer von zwei Orten dafür. Neu
 * angelegte Aufträge werden nach dem Speichern über `crm.projects.link`
 * angehängt, genau wie eine bestehende Datei oder Aufgabe.
 */
export function OrdersCard({ projectId, initialContactId, orders, onChanged }: Props) {
  const [anlegen, setAnlegen] = useState(false);
  const [bearbeiten, setBearbeiten] = useState<OrderRow | null>(null);
  const [loeschen, setLoeschen] = useState<OrderRow | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  const verknuepfen = async (neuer: OrderRow) => {
    setAnlegen(false);
    setFehler(null);
    try {
      await crm.projects.link(projectId, { orderIds: [neuer.id] });
      onChanged();
    } catch (err) {
      // Der Auftrag ist angelegt, hängt aber noch nicht am Projekt — ohne
      // sichtbaren Fehler sähe die Karte einfach so aus, als wäre nichts
      // passiert, und der Auftrag bliebe unbemerkt unverknüpft.
      setFehler(err instanceof Error ? err.message : 'Verknüpfen fehlgeschlagen');
    }
  };

  return (
    <Card className="p-4">
      <div className="flex items-center justify-between">
        <h2 className="flex items-center gap-2 text-sm font-semibold">
          <Receipt className="h-4 w-4 text-muted-foreground" />
          Aufträge
        </h2>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label="Auftrag anlegen"
          onClick={() => setAnlegen(true)}
        >
          <Plus className="h-4 w-4" />
        </Button>
      </div>

      {orders.length === 0 ? (
        <p className="mt-2 text-sm text-muted-foreground">Noch kein Auftrag am Projekt.</p>
      ) : (
        <ul className="mt-3 space-y-2">
          {orders.map((o) => (
            <li key={o.id} className="flex items-start justify-between gap-2 text-sm">
              <button
                type="button"
                className="min-w-0 flex-1 text-left hover:underline"
                onClick={() => setBearbeiten(o)}
              >
                <span className="block font-medium">{o.title}</span>
                <span className="block text-xs text-muted-foreground">
                  {o.number}
                  {o.valueCents > 0 && ` · ${formatEuro(o.valueCents)}`}
                </span>
              </button>
              <div className="flex shrink-0 items-center gap-1">
                <OrderStatusPill status={o.status as never} />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label={`${o.number} löschen`}
                  onClick={() => setLoeschen(o)}
                >
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
      {fehler && <p className="mt-2 text-xs text-danger">{fehler}</p>}

      {anlegen && (
        <OrderDialog
          order={null}
          initialContactId={initialContactId}
          onClose={() => setAnlegen(false)}
          onSaved={(neuer) => void verknuepfen(neuer)}
        />
      )}

      {bearbeiten && (
        <OrderDialog
          order={bearbeiten}
          onClose={() => setBearbeiten(null)}
          onSaved={() => {
            setBearbeiten(null);
            onChanged();
          }}
        />
      )}
      {loeschen && (
        <OrderDeleteDialog
          order={loeschen}
          onCancel={() => setLoeschen(null)}
          onDeleted={() => {
            setLoeschen(null);
            onChanged();
          }}
        />
      )}
    </Card>
  );
}
